import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
}                            from 'react';
import {
  IPhoto,
  TRotationDegree,
}                            from '@typings/auction';
import {
  EPhotoType,
  EAuctionStatus,
}                            from '@enums/auction';
import {
  useFetchAuctionPhotos,
  useUploadAuctionPhoto,
  useDeleteAuctionPhoto,
  useRotateAuctionPhoto,
}                            from '@modules/auctions/queries/auctionsQueries';
import { Loader }            from '@components/Loader/Loader';
import { AuctionPhotosPage } from '../pages/AuctionPhotosPage';

interface IProps {
  auctionId: string;
}

const EDIT_PHOTOS_STATUSES: EAuctionStatus[] = [
  EAuctionStatus.SavedDraft,
  EAuctionStatus.SavedDraftNoPhotos,
  EAuctionStatus.Pending,
  EAuctionStatus.PendingNoPhotos,
];

type TStatus = EAuctionStatus | null;

export const AuctionPhotosContainer = ({ auctionId }: IProps): JSX.Element => {
  const [photos, setPhotos] = useState<IPhoto[]>([]);
  const [status, setStatus] = useState<TStatus>(null);

  const isRemoveDisabled = useMemo(
    () => !status || (!EDIT_PHOTOS_STATUSES.includes(status) && photos.length <= 1),
    [status, photos],
  );

  // #region INTEGRATE API
  const { isLoading } = useFetchAuctionPhotos(
    auctionId,
    {
      enabled   : !!auctionId,
      onSuccess : (data) => {
        setPhotos(data);
      },
    },
  );

  const {
    mutateAsync : uploadPhoto,
    isLoading   : isLoadingUpload,
  } = useUploadAuctionPhoto({
    onSuccess: (data) => {
      const uploadedIdx = photos.findIndex((photo) => photo.photoType === data.photoType);

      const updatedPhotos: IPhoto[] = uploadedIdx !== -1
        ? photos.map((photo) => (photo.photoType === data.photoType ? data : photo))
        : [...photos, data];

      setPhotos(updatedPhotos);
    },
  });

  const {
    mutateAsync : deletePhoto,
    isLoading   : isLoadingDelete,
  } = useDeleteAuctionPhoto({
    onSuccess: (data) => {
      const updatedPhotos: IPhoto[] = photos.filter((photo) => (photo.id !== data));

      setPhotos(updatedPhotos);
    },
  });

  const {
    mutateAsync: rotatePhoto,
    isLoading   : isLoadingRotate,
  } = useRotateAuctionPhoto({
    onSuccess: (data) => {
      const updatedPhotos: IPhoto[] = photos.map((photo) => (photo.id === data.id ? data : photo));

      setPhotos(updatedPhotos);
    },
  });
  // #endregion

  const handleUploadPhoto = useCallback(
    async (
      photoType : EPhotoType,
      image     : File,
    ) => {
      await uploadPhoto({ auctionId, photoType, image });
    },
    [auctionId, uploadPhoto],
  );

  const handleRemovePhoto = useCallback(
    async (photoId: string) => {
      await deletePhoto({ auctionId, photoId });
    },
    [auctionId, deletePhoto],
  );

  const handleRotatePhoto = useCallback(
    async (
      photoId : string,
      currentDeg  : TRotationDegree,
    ) => {
      const nextDeg = currentDeg === 270 ? 0 : currentDeg + 90;

      await rotatePhoto({ auctionId, photoId, degree: nextDeg as TRotationDegree });
    },
    [auctionId, rotatePhoto],
  );

  useEffect(
    () => {
      const storageStatus = sessionStorage.getItem(auctionId);

      setStatus(storageStatus?.length ? JSON.parse(storageStatus) : null);

      return () => {
        sessionStorage.removeItem(auctionId);
      };
    },
    [auctionId],
  );

  if (isLoading) {
    return (<Loader />);
  }

  return (
    <AuctionPhotosPage
      photos            = {photos}
      handleUploadPhoto = {handleUploadPhoto}
      handleRemovePhoto = {handleRemovePhoto}
      handleRotatePhoto = {handleRotatePhoto}
      isButtonsDisabled = {isLoadingUpload || isLoadingRotate || isLoadingDelete}
      isRemoveDisabled  = {isRemoveDisabled}
    />
  );
};
